// SubscriptionTransferOutputSteps.tsx
import React from "react";
import { Box, Typography, Link, Tooltip } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import ErrorIcon from "@mui/icons-material/Error";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import WarningIcon from "@mui/icons-material/Warning";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import NotStartedIcon from "@mui/icons-material/NotStarted";
import LinkIcon from "@mui/icons-material/Link";
import { generateNetsuiteLink } from "utils/netsuite";
import { isProdHost } from "../../../util/helpers";

const isProd = () => isProdHost();

// Base Component
interface SubscriptionTransferStepDisplayBaseProps {
  name: string;
  status: string;
  /** Optional 1-based sequence number rendered as a numbered badge. */
  step?: number;
  /**
   * When true, the SKIPPED status is treated as an intentional skip (e.g.
   * same-day transfer skipping CM/invoice/JE) and shown with a neutral
   * dash icon and "Not applicable" tooltip instead of the warning icon.
   */
  intentionalSkip?: boolean;
  link?: string;
  children?: React.ReactNode;
}

const SubscriptionTransferStepDisplayBase: React.FC<SubscriptionTransferStepDisplayBaseProps> = ({
  name,
  status,
  step,
  intentionalSkip,
  link,
  children,
}) => {
  let icon;
  let color;
  let tooltipText;

  switch (status) {
    case "COMPLETED":
      icon = <CheckIcon />;
      color = "green";
      tooltipText = "Completed";
      break;
    case "FAILED":
    case "FAILURE":
      icon = <ErrorIcon />;
      color = "red";
      tooltipText = "Failed";
      break;
    case "IN_PROGRESS":
      icon = <HourglassEmptyIcon />;
      color = "orange";
      tooltipText = "In Progress";
      break;
    case "SKIPPED":
      // Intentional same-day skips are normal expected behavior, so render
      // them with a neutral dash icon rather than the warning triangle (which
      // implies "something went wrong"). Non-intentional skips (e.g. a skip
      // caused by an upstream short-circuit) still get the warning icon so
      // the operator can investigate.
      if (intentionalSkip) {
        icon = <RemoveCircleOutlineIcon />;
        color = "#9aa0a6";
        tooltipText = "Not applicable for this transfer";
      } else {
        icon = <WarningIcon />;
        color = "grey";
        tooltipText = "Skipped";
      }
      break;
    case "NOT_INITIATED":
    default:
      icon = <NotStartedIcon />;
      color = "grey";
      tooltipText = "Not Initiated";
      break;
  }

  return (
    <Box mb={2} width="300px" minHeight="60px">
      <Box
        display="flex"
        flexDirection="row"
        alignItems="center"
        justifyContent="left"
        gap={1}
        minHeight="32px"
      >
        {step != null && (
          <Box
            component="span"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              minWidth: "22px",
              height: "22px",
              padding: "0 6px",
              borderRadius: "11px",
              backgroundColor: "#e0e3e7",
              color: "#3c4043",
              fontSize: "0.7rem",
              fontWeight: 600,
              flexShrink: 0,
            }}
          >
            {step}
          </Box>
        )}
        <Tooltip title={tooltipText}>
          <Box
            component="span"
            color={color}
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            {icon}
          </Box>
        </Tooltip>
        {link && (
          <Link href={link} target="_blank" rel="noopener noreferrer">
            <LinkIcon />
          </Link>
        )}
        <Typography
          variant="body1"
          flexGrow={1}
          sx={{ fontWeight: "bold", marginRight: 1 }}
        >
          {name}
        </Typography>
      </Box>
      {children}
    </Box>
  );
};

interface CommonStepProps {
  name: string;
  status: string;
  step: number;
  displayId?: string;
  displayUrl?: string;
  /**
   * Step-specific outcome detail rendered below the displayId (e.g. invoice
   * total, "Adjusted CM by …", or "Note: N employee fields needed
   * remediation"). Kept separate from `displayId` so cards without an ID
   * (Reconcile, ApplyPayment without paymentId, etc.) can still surface
   * useful per-step context.
   */
  detail?: string;
  /** See `SubscriptionTransferStepDisplayBaseProps.intentionalSkip`. */
  intentionalSkip?: boolean;
  errorMessage?: string;
}

const CommonStep: React.FC<CommonStepProps> = ({
  name,
  status,
  step,
  displayId,
  displayUrl,
  detail,
  intentionalSkip,
  errorMessage,
}) => (
  <SubscriptionTransferStepDisplayBase
    name={name}
    status={status}
    step={step}
    intentionalSkip={intentionalSkip}
    link={
      displayUrl
        ? generateNetsuiteLink(
            isProd() ? "4914352" : "4914352-sb2",
            "base",
            "",
            "",
          ) + displayUrl
        : undefined
    }
  >
    {displayId && (
      <Typography variant="body2" sx={{ paddingTop: 1 }}>
        {displayId}
      </Typography>
    )}
    {detail && (
      <Typography
        variant="caption"
        component="div"
        sx={{ paddingTop: 0.5, color: "text.secondary" }}
      >
        {detail}
      </Typography>
    )}
    {errorMessage ? (
      <Typography
        variant="caption"
        component="div"
        sx={{ paddingTop: 0.5, color: "error.main", whiteSpace: "pre-wrap", wordBreak: "break-word" }}
      >
        {errorMessage}
      </Typography>
    ) : null}
  </SubscriptionTransferStepDisplayBase>
);


export {
  SubscriptionTransferStepDisplayBase,
  CommonStep,
};
